import { useState } from 'react';
import { useSelector } from 'react-redux';
import {
    Box,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    Heading,
    Alert,
    AlertIcon,
    AlertDescription,
} from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import { Button } from 'components/v4';
import { breadCrumbStyling } from 'components/forms/new/formStyles';
import { toast } from 'react-toastify';

import { validationSchema } from './consts';
import { createSite } from './api';
import TextField from './formFields/TextField';
import SelectField from './formFields/SelectField';
import walkthroughIds from './walkthroughIds';

const getInitialValues = (prefill) => ({
    name: prefill?.name || '',
    description: prefill?.description || '',
    address: prefill?.addressID || prefill?.address || '',
    pbx: prefill?.pbx || '',
});

const Create = ({
    onBack,
    onSuccess,
    prefill,
    serviceId,
    companyId,
    platformId,
    requiredScope,
    addresses = [],
    pbxOptions = [],
}) => {
    const { darkMode } = useSelector((s) => s.settings);
    const [error, setError] = useState(null);

    const addressOptions = addresses.map((a) => ({
        value: a.id,
        label: a.description || a.companyName || a.id,
    }));

    const handleSubmit = async (values, { setSubmitting }) => {
        setError(null);
        try {
            await createSite(
                values,
                companyId,
                serviceId,
                platformId,
                requiredScope,
            );
            onSuccess();
        } catch (err) {
            const message =
                err?.response?.data?.message ||
                err?.message ||
                'An error occurred. Please try again.';
            setError(message);
            toast.error(message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box>
            <Breadcrumb sx={breadCrumbStyling} mb={4}>
                <BreadcrumbItem>
                    <BreadcrumbLink as="button" onClick={onBack}>
                        Sites
                    </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbItem isCurrentPage>
                    <BreadcrumbLink>Add Site</BreadcrumbLink>
                </BreadcrumbItem>
            </Breadcrumb>

            <Heading size="lg" mb={2}>
                Add Site
            </Heading>
            <Box color="gray.500" mb={6} fontSize="sm">
                Configure a new 8×8 site. Each site is linked to one address and
                one PBX.
            </Box>

            {error && (
                <Alert status="error" mb={4}>
                    <AlertIcon />
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            )}

            <Formik
                initialValues={getInitialValues(prefill)}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                validateOnChange={false}
                validateOnBlur>
                {({ isSubmitting }) => (
                    <Form>
                        <Box
                            bg={darkMode ? 'dark.tcap' : 'white'}
                            borderRadius={15}
                            p={8}
                            maxW={895}
                            boxShadow="sm">
                            <TextField
                                name="name"
                                label="Site Name"
                                description="A friendly name to identify this site."
                                required
                                darkMode={darkMode}
                                walkthroughId={walkthroughIds.ufSiteName}
                                placeholder="e.g. Melbourne HQ"
                            />
                            <TextField
                                name="description"
                                label="Description"
                                description="Optional description for this site."
                                darkMode={darkMode}
                                placeholder="e.g. Main office location"
                            />
                            <SelectField
                                name="address"
                                label="Address"
                                description="The physical address the site is associated with."
                                options={addressOptions}
                                darkMode={darkMode}
                                walkthroughId={walkthroughIds.ufAddress}
                                placeholder="Please select"
                            />
                            <SelectField
                                name="pbx"
                                label="PBX"
                                description="The 8×8 PBX this site connects to."
                                options={pbxOptions}
                                darkMode={darkMode}
                                walkthroughId={walkthroughIds.ufPbx}
                                placeholder="Please select"
                            />
                        </Box>

                        <Box display="flex" gap={3} mt={6} maxW={895}>
                            <Button
                                type="submit"
                                isLoading={isSubmitting}
                                loadingText="Saving"
                                data-walkthroughid={
                                    walkthroughIds.ufAddSiteBtn
                                }>
                                Save
                            </Button>
                            <Button
                                variant="outline"
                                bg="white"
                                onClick={onBack}
                                isDisabled={isSubmitting}
                                data-walkthroughid={walkthroughIds.ufBackBtn}>
                                Cancel
                            </Button>
                        </Box>
                    </Form>
                )}
            </Formik>
        </Box>
    );
};

export default Create;
